import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { arrayProblems } from '../../data/practice/arrayProblems';
import ProblemLayout from '../../components/ProblemLayout';

interface TestResult {
  passed: boolean;
  input: string;
  expected: string;
  actual: string;
  message?: string;
}

interface TestCase {
  input: any[];
  output: any;
}

const ArrayProblem: React.FC = () => {
  const { problemId } = useParams<{ problemId: string }>();
  const [code, setCode] = useState<string>('');
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [aiFeedback, setAiFeedback] = useState<string>('');

  const problem = arrayProblems.find(p => p.id === problemId);

  useEffect(() => {
    if (problemId) {
      setCode(problem?.starterCode || '');
      setTestResults([]);
      setAiFeedback('');
    }
  }, [problemId, problem]);

  const handleRunTests = () => {
    if (!problem) return;
    
    try {
      const userFunction = new Function('return ' + code)();
      const results: TestResult[] = [];
      
      (problem.testCases as TestCase[]).forEach((testCase: TestCase) => {
        try {
          const actual = userFunction(...testCase.input);
          const passed = JSON.stringify(actual) === JSON.stringify(testCase.output);
          results.push({
            passed,
            input: JSON.stringify(testCase.input),
            expected: JSON.stringify(testCase.output),
            actual: JSON.stringify(actual),
            message: passed ? 'Test passed!' : 'Test failed'
          });
        } catch (err) {
          const error = err as Error;
          results.push({
            passed: false,
            input: JSON.stringify(testCase.input),
            expected: JSON.stringify(testCase.output),
            actual: 'Error',
            message: `Runtime error: ${error.message}`
          });
        }
      });
      
      setTestResults(results);
      generateAiFeedback(results);
    } catch (err) {
      const error = err as Error;
      setTestResults([{
        passed: false,
        input: 'N/A',
        expected: 'N/A',
        actual: 'Error',
        message: `Compilation error: ${error.message}`
      }]);
    }
  };

  const generateAiFeedback = (results: TestResult[]) => {
    const failedTests = results.filter(r => !r.passed);
    if (failedTests.length === 0) {
      setAiFeedback('Great job! All test cases passed. Your solution is working correctly.');
    } else {
      const feedback = `Here are some suggestions for the failed test cases:
${failedTests.map((test, i) => `
Test Case ${i + 1}:
- Input: ${test.input}
- Expected: ${test.expected}
- Your Output: ${test.actual}
- Possible Issue: ${getTestFeedback(test)}`).join('\n')}`;
      setAiFeedback(feedback);
    }
  };

  const getTestFeedback = (test: TestResult): string => {
    if (test.actual === 'Error') {
      return 'Your code threw an error. Make sure to handle edge cases and check for null/undefined values.';
    }
    if (test.expected === test.actual) {
      return 'The output format might be different from what\'s expected.';
    }
    return 'Your logic might need revision. Compare your output with the expected result.';
  };

  if (!problem) {
    return <div>Problem not found</div>;
  }

  return (
    <ProblemLayout 
      title={problem.title}
      difficulty={problem.difficulty}
      tags={problem.tags}
      description={problem.description}
      hints={problem.hints}
      code={code}
      onCodeChange={setCode}
      onResetCode={() => setCode(problem.starterCode || '')}
      backUrl="/practice/arrays"
      problemId={problemId || ''}
    />
  );
};

export default ArrayProblem;
